/**
 * Formuláře – vylepšení všech <select> knihovnou Choices.js.
 *
 * Choices je globál z vendor bundle (js/vendor/choices/choices.min.js).
 * Opt-out: na select stačí dát atribut data-no-choices.
 */
/**
 * reCAPTCHA v3 – knihovna od Googlu má přes 300 kB, tak ji stahujeme až ve chvíli,
 * kdy návštěvník poprvé sáhne na formulář. Kdo si jen prohlédne stránku, nezaplatí
 * za ni ani bajt. Klíč vykreslilo PHP do <meta>.
 */
import { loadVendor } from './lazy-vendor.js';

const recaptchaKey = () => document.querySelector('meta[name="recaptcha-site-key"]')?.content || '';

let recaptchaLoading = null;

function loadRecaptcha() {
    const key = recaptchaKey();
    if (!key) return null;                       // ochrana je vypnutá
    if (recaptchaLoading) return recaptchaLoading;

    recaptchaLoading = new Promise((resolve) => {
        const el = document.createElement('script');
        el.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(key)}`;
        el.async = true;
        el.onload  = () => resolve(true);
        el.onerror = () => resolve(false);       // výpadek Googlu neshodí odeslání
        document.head.appendChild(el);
    });
    return recaptchaLoading;
}

/**
 * Token pro odeslání formuláře.
 *
 * Vrací null, když je ochrana vypnutá nebo když si Google token vydat nedokáže –
 * formulář se pak odešle bez tokenu a rozhodne server. Když návštěvník odešle
 * formulář dřív, než se knihovna stáhla, počkáme si tady na ni.
 */
async function recaptchaToken() {
    const key = recaptchaKey();
    if (!key) return null;

    const ok = await loadRecaptcha();
    if (!ok || typeof grecaptcha === 'undefined') return null;

    try {
        await new Promise((resolve) => grecaptcha.ready(resolve));
        return await grecaptcha.execute(key, { action: 'lead_submit' });
    } catch (e) {
        return null;
    }
}


const CHOICES = '/www/assets/js/vendor/choices/choices.min.js';

/**
 * Vylepšené selecty. Knihovna (21 kB gzip) se stahuje až když návštěvník sáhne
 * na formulář — do té doby fungují nativní <select>, jen bez našeho vzhledu.
 */
async function initChoices() {
    const selecty = [...document.querySelectorAll('select')]
        .filter((s) => !s.hasAttribute('data-no-choices') && !s.closest('.choices'));
    if (!selecty.length) return;

    if (!await loadVendor(CHOICES)) return;   // nedorazila → nativní select zůstane
    if (typeof Choices === 'undefined') return;

    selecty.forEach((select) => {
        new Choices(select, {
            searchEnabled: select.options.length > 8, // hledání jen u delších seznamů
            shouldSort: false,                         // zachovat pořadí <option>
            allowHTML: false,
            itemSelectText: '',                        // pryč "Press to select"
            loadingText: 'Načítání…',
            noResultsText: 'Žádné výsledky',
            noChoicesText: 'Žádné možnosti',
        });

        // Choices obalí select divem s role="listbox" a původní <select> nechá
        // uvnitř jako nosič hodnoty. Pro odečítače je to rozbitý listbox –
        // role="listbox" smí obsahovat jen option/group, ne select (axe:
        // aria-required-children). Vlastní přístupné UI staví Choices sám,
        // takže původní select z přístupnostního stromu schováme; formuláři
        // to nevadí, hodnotu odešle dál.
        select.setAttribute('aria-hidden', 'true');
        select.setAttribute('tabindex', '-1');
    });
}

/**
 * Přílohy u poptávky — append místo replace + odebírání jednotlivých souborů.
 * Nativní <input type=file multiple> při novém výběru předchozí soubory zahodí a
 * neumí odebrat jednu položku. Držíme vlastní seznam a přes DataTransfer ho zrcadlíme
 * zpět do input.files, aby se odeslal serveru. Limity jsou jen UX — server je autorita.
 */
function initFileInputs() {
    document.querySelectorAll('.form-group input[type="file"]').forEach((input) => {
        if (input.dataset.fileEnhanced) return;
        input.dataset.fileEnhanced = '1';

        const maxFiles = parseInt(input.dataset.maxFiles || '0', 10) || Infinity;
        const maxSize  = parseInt(input.dataset.maxSize || '0', 10) || Infinity;
        const group    = input.closest('.form-group');
        const list     = group ? group.querySelector('.form-file-list') : null;
        const errBox   = group ? group.querySelector('.form-file-error') : null;
        const files    = [];

        const fmtSize = (b) => (b >= 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(b / 1024)) + ' kB');
        const keyOf   = (f) => f.name + '|' + f.size + '|' + f.lastModified;

        function showError(msg) {
            if (!errBox) return;
            errBox.textContent = msg || '';
            errBox.hidden = !msg;
        }

        function sync() {
            const dt = new DataTransfer();
            files.forEach((f) => dt.items.add(f));
            input.files = dt.files; // přiřazení programově nespustí change
        }

        function renderList() {
            if (!list) return;
            list.textContent = '';
            files.forEach((f, i) => {
                const li   = document.createElement('li');
                li.className = 'form-file-item';

                const name = document.createElement('span');
                name.className = 'form-file-item__name';
                name.textContent = f.name;

                const size = document.createElement('span');
                size.className = 'form-file-item__size';
                size.textContent = fmtSize(f.size);

                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'form-file-remove';
                btn.setAttribute('aria-label', 'Odebrat přílohu ' + f.name);
                btn.textContent = '×';
                btn.addEventListener('click', () => {
                    files.splice(i, 1);
                    sync();
                    renderList();
                    showError('');
                });

                li.append(name, size, btn);
                list.appendChild(li);
            });
        }

        input.addEventListener('change', () => {
            const incoming = Array.from(input.files || []);
            let err = '';
            incoming.forEach((f) => {
                if (files.some((e) => keyOf(e) === keyOf(f))) return; // už přidaný
                if (f.size > maxSize) { err = 'Soubor „' + f.name + '" je příliš velký.'; return; }
                if (files.length >= maxFiles) { err = 'Přiložit lze nejvýš ' + maxFiles + ' souborů.'; return; }
                files.push(f);
            });
            showError(err);
            sync();
            renderList();
        });
    });
}

/**
 * Validace formulářů označených atributem data-validate.
 *
 * Formulář má novalidate, protože nativní bubliny prohlížeče nejdou nastylovat,
 * nedají se přečíst odečítačem na vyžádání a nejsou konzistentně česky. Chyby
 * proto píšeme jako text k poli.
 *
 * Přístupnost (WCAG):
 *  - 3.3.1 Error Identification – chyba je text u pole + souhrn v role="alert"
 *  - 3.3.3 Error Suggestion – hláška říká, co s polem udělat
 *  - 1.4.1 Use of Color – chyba nikdy jen barvou, vždy i textem
 *  - 2.4.3 Focus Order – fokus skočí na první chybné pole
 */
function initFormValidation() {
    document.querySelectorAll('form[data-validate]').forEach((form) => {
        const summary = form.querySelector('.form-summary');

        // Hláška patří k poli (#id-error). U zaškrtávátek a přepínačů je jedna
        // pro celou skupinu, takže ji hledáme i v obalu pole.
        const errorBoxOf = (field) =>
            form.querySelector(`#${CSS.escape(field.id)}-error`)
            || field.closest('.form-group')?.querySelector('.form-error')
            || null;

        const setError = (field, message) => {
            const box = errorBoxOf(field);
            field.setAttribute('aria-invalid', 'true');
            field.classList.add('is-invalid');
            if (box) {
                box.textContent = message;
                box.hidden = false;
            }
        };

        const clearError = (field) => {
            const box = errorBoxOf(field);
            field.removeAttribute('aria-invalid');
            field.classList.remove('is-invalid');
            if (box) {
                box.textContent = '';
                box.hidden = true;
            }
        };

        // telefon bereme benevolentně (mezery, závorky, předvolba),
        // hlídáme jen to, že v něm je aspoň 9 číslic
        const isPhone = (value) => (value.match(/\d/g) || []).length >= 9;
        const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(value.trim());

        const labelOf = (field) => {
            const label = form.querySelector(`label[for="${CSS.escape(field.id)}"]`);
            const legend = field.closest('.form-group')?.querySelector('legend');
            const text = label?.textContent || legend?.textContent || '';
            return text.replace('*', '').trim() || 'pole';
        };

        const validateField = (field) => {
            // Zaškrtávátka a přepínače: povinné = aspoň jedno zaškrtnuté.
            // Skupinu drží pohromadě data-required-group (u jednoho pole stačí required).
            if (field.type === 'checkbox' || field.type === 'radio') {
                const group = field.dataset.requiredGroup;
                const povinne = group ? true : field.hasAttribute('required');
                if (!povinne) { clearError(field); return true; }

                const members = group
                    ? Array.from(form.querySelectorAll(`[data-required-group="${CSS.escape(group)}"]`))
                    : [field];
                if (members.some((m) => m.checked)) { clearError(field); return true; }

                setError(field, field.dataset.errorRequired || `Vyberte prosím „${labelOf(field)}".`);
                return false;
            }

            const value = field.value.trim();

            if (field.hasAttribute('required') && !value) {
                // konkrétní hlášku si pole nese v data-error-required (skloňování),
                // jinak sáhneme po labelu – ten je vždycky po ruce
                setError(field, field.dataset.errorRequired || `Vyplňte prosím pole „${labelOf(field)}".`);
                return false;
            }

            // Hlášku o špatném tvaru si taky může nastavit redaktor (data-error-format).
            if (field.type === 'tel' && value && !isPhone(value)) {
                setError(field, field.dataset.errorFormat || 'Zadejte telefon včetně předvolby, například +420 605 199 144.');
                return false;
            }

            if (field.type === 'email' && value && !isEmail(value)) {
                setError(field, field.dataset.errorFormat || 'Zadejte e-mail ve tvaru jmeno@domena.cz.');
                return false;
            }

            if (field.type === 'url' && value && !/^https?:\/\/[^\s.]+\.[^\s]{2,}$/i.test(value)) {
                setError(field, field.dataset.errorFormat || 'Zadejte adresu i s https://, například https://www.example.cz.');
                return false;
            }

            if (field.type === 'number' && value && Number.isNaN(Number(value))) {
                setError(field, field.dataset.errorFormat || 'Zadejte prosím číslo.');
                return false;
            }

            clearError(field);
            return true;
        };

        // Skupina zaškrtávátek nebo přepínačů se kontroluje jako jeden celek –
        // jinak by se jedna chybějící odpověď počítala tolikrát, kolik má možností.
        const videneSkupiny = new Set();
        const fields = Array.from(form.querySelectorAll('input, select, textarea'))
            .filter((field) => field.id && field.type !== 'submit')
            .filter((field) => {
                const group = field.dataset.requiredGroup;
                if (!group) return true;
                if (videneSkupiny.has(group)) return false;
                videneSkupiny.add(group);
                return true;
            });

        // chybu shazujeme až při opravě, ne při psaní prvního znaku
        fields.forEach((field) => {
            field.addEventListener('blur', () => {
                if (field.getAttribute('aria-invalid') === 'true') validateField(field);
            });
            field.addEventListener('input', () => {
                if (field.getAttribute('aria-invalid') === 'true' && field.value.trim()) clearError(field);
            });
        });

        // Zaškrtnutí kterékoli možnosti ve skupině shodí chybu celé skupiny.
        form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((box) => {
            box.addEventListener('change', () => {
                const group = box.dataset.requiredGroup;
                const zastupce = group
                    ? fields.find((f) => f.dataset.requiredGroup === group) || box
                    : box;
                if (zastupce.getAttribute('aria-invalid') === 'true') validateField(zastupce);
            });
        });

        // První dotek formuláře = signál, že se nejspíš bude odesílat. Knihovnu
        // stáhneme už teď, ať na ni odeslání nečeká. { once: true } – stačí jednou.
        form.addEventListener('focusin', () => loadRecaptcha(), { once: true });

        form.addEventListener('submit', async (event) => {
            const invalid = fields.filter((field) => !validateField(field));

            if (summary) {
                summary.hidden = invalid.length === 0;
                summary.textContent = invalid.length
                    ? `Formulář se nepodařilo odeslat, zkontrolujte prosím ${invalid.length === 1 ? 'označené pole' : 'označená pole'} (${invalid.length}).`
                    : '';
            }

            if (invalid.length) {
                event.preventDefault();
                invalid[0].focus();
                return;
            }

            // Poptávka jde do CMS (modul Leady) přes /api/lead/submit, který
            // odpovídá JSON. Odesíláme na pozadí, ať uživatel nepřijde o stránku.
            event.preventDefault();
            const button = form.querySelector('button[type="submit"]');
            const done   = form.querySelector('[data-form-success]');
            if (button) button.disabled = true;

            const payload = new FormData(form);

            // reCAPTCHA v3 – token si říkáme až tady, ne při načtení stránky:
            // platí jen dvě minuty a rozepsaná poptávka bývá delší. Když skript
            // není (vypnutá ochrana, výpadek Googlu), odešleme bez tokenu –
            // o tom, jestli poptávku přijmout, rozhoduje server.
            const token = await recaptchaToken();
            if (token) payload.append('g-recaptcha-response', token);

            try {
                const res  = await fetch(form.action, {
                    method: 'POST',
                    body: payload,
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                });
                const data = await res.json().catch(() => ({}));

                if (res.ok && data.ok) {
                    form.reset();
                    if (done) {
                        done.hidden = false;
                        done.focus();          // odečítač oznámí potvrzení (WCAG 4.1.3)
                    }
                    if (summary) summary.hidden = true;
                } else if (summary) {
                    summary.hidden = false;
                    summary.textContent = data.error || 'Formulář se nepodařilo odeslat. Zkuste to prosím znovu nebo nám zavolejte.';
                }
            } catch (e) {
                if (summary) {
                    summary.hidden = false;
                    summary.textContent = 'Formulář se nepodařilo odeslat. Zkuste to prosím znovu nebo nám zavolejte.';
                }
            } finally {
                if (button) button.disabled = false;
            }
        });
    });
}

/**
 * Choices spustíme, až když se select doopravdy blíží na obrazovku. Formuláře
 * bývají dole pod ohybem, takže při načtení stránky by knihovna jen brala pásmo
 * obrázku v hlavičce. rootMargin 200px = načte se kousek předtím, než na něj
 * návštěvník doscrolluje, takže o čekání nezavadí.
 *
 * Bez IntersectionObserveru (staré prohlížeče) se prostě spustí rovnou.
 */
function planChoices() {
    const selecty = document.querySelectorAll('select:not([data-no-choices])');
    if (!selecty.length) return;

    if (!('IntersectionObserver' in window)) { initChoices(); return; }

    const pozorovatel = new IntersectionObserver((zaznamy, obs) => {
        if (!zaznamy.some((z) => z.isIntersecting)) return;
        obs.disconnect();
        initChoices();
    }, { rootMargin: '200px' });

    selecty.forEach((s) => pozorovatel.observe(s));
}

document.addEventListener('DOMContentLoaded', () => {
    planChoices();
    initFileInputs();
    initFormValidation();
});
